import React from "react";
import moment from "moment";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Moment } from "moment";
import { bindActionCreators, Dispatch } from "redux";
import { Action } from "typescript-fsa";
import { FormattedMessage } from "react-intl";
import { Label, Input, Button } from "reactstrap";

import { AuthState } from "@auth/models";
import { FilesState } from "@files/models";
import { Locale, LocalisationState } from "@localisation/models";
import { getLocale } from "@localisation/selectors";
import { CountrySelect } from "@localisation/components/CountrySelect";
import { MusicState } from "@music/models";
import { makeGetAlbum } from "@music/selectors";
import { PresetDateRangePicker } from "@ui/PresetDateRangePicker";
import * as actions from "../../actions";
import {
    getSettingFrom,
    getSettingTo,
    getSettingType,
    getSettingCountries,
    MusicIdProps,
} from "../../selectors";
import { SalesState } from "../../models";
import { getEarliestDate, getLatestDate, getDatePresets, DateRange, MAX_FILTER_DAYS } from "../../constants";
import { messages } from "../../messages";

// The Presenter
interface SalesControlsStateProps {
    from: Moment;
    to: Moment;
    type: "stream" | "download";
    countries: string[];
    locale: Locale;
    presets: DateRange[];
}

interface SalesControlsDispatchProps {
    setDateRange(p: [Moment, Moment]): Action<[Moment, Moment]>;
    setCountries(p: string[]): Action<string[]>;
    toggleStreamsDownloads(p: {}): Action<{}>;
}

type Props = SalesControlsStateProps & SalesControlsDispatchProps & MusicIdProps;

interface SalesControlsState {
    earliestDate: Moment;
    latestDate: Moment;
    from: Moment;
    to: Moment;
}

class SalesControls extends React.Component<Props, SalesControlsState>{
    private interval: NodeJS.Timer;

    constructor(props: Props) {
        super(props);

        // Set the dates.
        moment.locale(props.locale);

        this.state = {
            earliestDate: getEarliestDate(),
            latestDate: getLatestDate(),
            from: null,
            to: null,
        };
    }

    public componentDidMount() {
        // Tick every minute
        this.interval = setInterval(() => this.tick(), 1000 * 60);
    }

    public componentWillUnmount() {
        clearInterval(this.interval);
    }

    public componentWillReceiveProps(nextProps: Props) {
        moment.locale(nextProps.locale);
    }

    private tick() {
        this.setState({
            earliestDate: getEarliestDate(),
            latestDate: getLatestDate(),
        });
    }

    private updateDates(startDate: Moment, endDate: Moment) {
        this.setState({
            from: startDate,
            to: endDate,
        });
    }

    private onClose(startDate: Moment, endDate: Moment) {
        this.props.setDateRange([startDate, endDate]);
        this.setState({
            from: null,
            to: null,
        });
    }

    private onTypeChange() {
        this.props.toggleStreamsDownloads({});
    }

    private onCountryChange(codes: string | string[]) {
        if (!Array.isArray(codes)) {
            codes = [codes];
        }
        this.props.setCountries(codes);
    }

    public render() {
        const { type, countries, presets } = this.props;
        const { earliestDate, latestDate } = this.state;
        const from = this.state.from != null ? this.state.from : this.props.from;
        const to = this.state.to != null ? this.state.to : this.props.to;

        return (
            <div className="sales-controls">
                <div className="sales-controls__type-toggle">
                    <div className="switch-toggle">
                        <Input type="radio"
                            id="sales_type_stream"
                            name="sales_type"
                            onChange={() => this.onTypeChange()}
                            checked={type === "stream"}
                            />
                        <Label for="sales_type_stream">
                            <FormattedMessage {...messages.salesStreams} />
                        </Label>

                        <Input type="radio"
                            id="sales_type_download"
                            name="sales_type"
                            onChange={() => this.onTypeChange()}
                            checked={type === "download"}
                            />
                        <Label for="sales_type_download">
                            <FormattedMessage {...messages.salesDownloads} />
                        </Label>

                        <Button tag="a" className="rounded-0" color="primary" />
                    </div>
                </div>

                <div className="sales-controls__date-picker">
                    <PresetDateRangePicker
                        startDate={from}
                        startDateId="sales-controls-from"
                        endDate={to}
                        endDateId="sales-controls-to"
                        onDatesChange={({ startDate, endDate }) => this.updateDates(startDate, endDate)}
                        onClose={({ startDate, endDate }) => this.onClose(startDate, endDate)}
                        displayFormat="DD/MM/YYYY"
                        anchorDirection="right"
                        presets={presets}
                        earliestDate={earliestDate}
                        latestDate={latestDate}
                        maxDays={MAX_FILTER_DAYS}
                    ></PresetDateRangePicker>
                </div>

                <div className="sales-controls__country-select">
                    <CountrySelect
                        multiple={true}
                        selected={countries}
                        onChange={(code) => this.onCountryChange(code)}
                        placeholderMessage={messages.filterCountries}
                        />
                </div>
            </div>
        );
    }
}

// Connected Component
type State = SalesState & LocalisationState & MusicState & FilesState & AuthState;

const makeMapStateToProps = () => {
    const getAlbum = makeGetAlbum();
    return (state: State, props: MusicIdProps): SalesControlsStateProps => {
        let album = null;
        if (props.albumId != null) {
            album = getAlbum(state, {
                params: {
                    id: String(props.albumId),
                },
            });
        }
        return {
            from: getSettingFrom(state),
            to: getSettingTo(state),
            type: getSettingType(state),
            countries: getSettingCountries(state),
            locale: getLocale(state),
            presets: getDatePresets(album),
        };
    };
};

const mapDispatchToProps = (dispatch: Dispatch<State>): SalesControlsDispatchProps => {
    return {
        setDateRange: bindActionCreators(actions.setDateRange, dispatch),
        toggleStreamsDownloads: bindActionCreators(actions.toggleStreamsDownloads, dispatch),
        setCountries: bindActionCreators(actions.setCountries, dispatch),
    };
};

const ConnectedSalesControls = compose(
    connect(makeMapStateToProps, mapDispatchToProps),
)(SalesControls);

export {
    SalesControls as SalesControlsPresenter,
    ConnectedSalesControls as SalesControls,
    Props as SalesControlsProps,
};
